/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  belongsTo,
  attr,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import type DepositSlip from 'linced-app/models/finance/deposit-slip';
import type TillSession from 'linced-app/models/finance/till-session';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// Proto: finance.Discrepancy.
export default class Discrepancy extends Model {
  declare [Type]: 'finance/discrepancy';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  // personId is the ID of the Person who created this Discrepancy.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  // tillSessionId is the Till Session to which this Discrepancy relates.
  @belongsTo('finance/till-session', { async: true, inverse: 'discrepancies' })
  declare tillSessionId: AsyncBelongsTo<TillSession>;

  @alias('tillSessionId')
  declare tillSession: AsyncBelongsTo<TillSession>;

  // depositSlipId is the Deposit Slip to which this Discrepancy relates.
  @belongsTo('finance/deposit-slip', { async: true, inverse: 'discrepancies' })
  declare depositSlipId: AsyncBelongsTo<DepositSlip>;

  @alias('depositSlipId')
  declare depositSlip: AsyncBelongsTo<DepositSlip>;

  @alias('depositSlip.id')
  declare depositSlip_id: string | null | undefined;

  // amount is the amount of this Discrepancy.
  @attr('number', { defaultValue: 0 })
  declare amount: number;

  // reason is the explanation for this Discrepancy.
  @attr('string', { defaultValue: '' })
  declare reason: string;

  // method is the payment method to which this Discrepancy applies.
  @attr('number', { defaultValue: 0 })
  declare method: EnumValue<typeof PaymentMethod> | 0;

  // acknowledgedById is the ID of the person who acknowledged this
  // Discrepancy.
  @belongsTo('person', { async: true, inverse: null })
  declare acknowledgedById: AsyncBelongsTo<Person>;

  @alias('acknowledgedById')
  declare acknowledgedBy: AsyncBelongsTo<Person>;

  // acknowledgedAt is the unix timestamp of when this Discrepancy was
  // acknowledged.
  @attr('number', { defaultValue: 0 })
  declare acknowledgedAt: number;

  // isAcknowledged provides a convenient boolean indicator of whether this
  // Deposit Slip has been acknowledged.
  @computed('acknowledgedAt')
  get isAcknowledged() {
    return Boolean(this.acknowledgedAt);
  }
}
